import {
  FetchRequest,
  JsonRpcApiProviderOptions,
  JsonRpcPayload,
  JsonRpcProvider,
  JsonRpcResult,
  Network,
  TransactionReceipt
} from 'ethers'

// Errors thrown by `JsonRpcProvider._send` are always transport/HTTP-level failures
// (connection refused, DNS error, client-side timeout, or a non-2xx response such as
// 429/5xx caught by `assertOk`). JSON-RPC *execution* errors (e.g. reverts, "method
// not found") are NOT thrown here — they come back as `{ error }` objects inside the
// result array and are handled upstream. Therefore any throw from a single-endpoint
// attempt is a safe signal to fail over to the next RPC URL without masking real
// contract-level failures.

// Cap a single attempt so a hung/slow RPC fails over promptly instead of blocking on
// ethers' 300s default. Balance/state/gas calls all complete well within this.
const DEFAULT_PER_REQUEST_TIMEOUT = 30000
// After failing over to a backup, keep using it for this long before re-probing the
// primary, so we don't hammer a down primary on every request but still return to it
// automatically once it recovers.
const PRIMARY_RECOVERY_MS = 60000

/**
 * A `JsonRpcProvider` that transparently fails over across a list of RPC URLs.
 *
 * Behaviour:
 * - Requests start at the primary (index 0 = `selectedRpcUrl`). When the primary is
 *   healthy there is zero overhead — the first attempt succeeds and returns.
 * - On a transport-level failure the request is retried against the next URL in the
 *   list until one answers or all are exhausted (then the last error is thrown, so we
 *   never swallow a genuine outage).
 * - After a failover, subsequent requests start from the last known-good URL for
 *   `PRIMARY_RECOVERY_MS`, then the primary is re-probed so we return to it once it is
 *   healthy again.
 *
 * `_getConnection()` is intentionally left untouched (still returns the primary URL) so
 * external callers that read `provider._getConnection().url` — e.g.
 * `ProvidersController.setProvider` deciding whether the RPC changed — keep behaving
 * exactly as with a plain `JsonRpcProvider`.
 */
export class FailoverJsonRpcProvider extends JsonRpcProvider {
  readonly #urls: string[]

  readonly #network?: Network

  readonly #perRequestTimeout: number

  readonly #chainIdLabel: string

  // Index of the URL that following requests should start from (0 = primary).
  #activeIndex = 0

  // Timestamp of the last failover, used to decide when to re-probe the primary.
  #lastSwitchAt = 0

  constructor(
    urls: string[],
    network?: Network,
    options?: JsonRpcApiProviderOptions,
    perRequestTimeout: number = DEFAULT_PER_REQUEST_TIMEOUT
  ) {
    // Build the base provider against the primary URL so `_getConnection().url` and
    // static-network pinning behave identically to a plain JsonRpcProvider.
    super(urls[0], network, options)
    this.#urls = urls
    this.#network = network
    this.#perRequestTimeout = perRequestTimeout
    this.#chainIdLabel = network ? String(network.chainId) : 'unknown'
  }

  // The order in which URLs are tried for the next request. Starts at `#activeIndex`
  // and wraps around, so a promoted backup is preferred until the primary recovers.
  #buildOrder(): number[] {
    if (this.#activeIndex !== 0 && Date.now() - this.#lastSwitchAt > PRIMARY_RECOVERY_MS) {
      this.#activeIndex = 0
    }
    const n = this.#urls.length
    return Array.from({ length: n }, (_, i) => (this.#activeIndex + i) % n)
  }

  // eslint-disable-next-line no-underscore-dangle
  async _send(payload: JsonRpcPayload | JsonRpcPayload[]): Promise<JsonRpcResult[]> {
    const order = this.#buildOrder()
    let lastError: any

    for (const idx of order) {
      const url = this.#urls[idx]
      if (!url) continue

      const request = new FetchRequest(url)
      request.body = JSON.stringify(payload)
      request.setHeader('content-type', 'application/json')
      request.timeout = this.#perRequestTimeout

      try {
        // eslint-disable-next-line no-await-in-loop
        const response = await request.send()
        response.assertOk()
        let resp = response.bodyJson
        if (!Array.isArray(resp)) resp = [resp]

        // Promote the URL that answered so subsequent requests start there for a while.
        if (idx !== this.#activeIndex) {
          this.#activeIndex = idx
          this.#lastSwitchAt = Date.now()
          console.warn(
            `[FailoverRPC] chain ${this.#chainIdLabel}: switched to ${url}` +
              (lastError ? ` after error: ${lastError?.message || lastError}` : '')
          )
        }

        return resp
      } catch (error: any) {
        // Transport-level failure — remember it and try the next URL. If this was the
        // last URL, the error is rethrown below so the caller sees the real outage.
        lastError = error
      }
    }

    throw lastError
  }

  /**
   * Ask every OTHER endpoint for a receipt the active one claims not to have.
   *
   * `_send` only fails over on transport-level errors, so a node that answers
   * `200 {"result": null}` is treated as healthy and keeps being used. A lagging
   * or pruned endpoint therefore hides a receipt forever: the activity poller
   * asks the same node every 5s, gets `null` every time, and the txn stays
   * "pending" in the UI for days even though it was mined (observed on
   * PulseChain - see docs/transactions.md). A `null` receipt is not proof the
   * txn is unmined, only that THIS node does not have it, so before drawing any
   * conclusion we ask the rest of the list.
   *
   * Returns the first non-null receipt found, or null if no endpoint has one -
   * which is then a trustworthy "not mined yet".
   */
  async getTransactionReceiptFromOtherUrls(txnId: string): Promise<TransactionReceipt | null> {
    const order = this.#buildOrder().filter((idx) => idx !== this.#activeIndex)

    for (const idx of order) {
      const url = this.#urls[idx]
      if (!url) continue

      // A throwaway provider so ethers does the receipt formatting for us and the
      // probe cannot disturb the shared provider's failover state mid-flight.
      // This is the one place where building a provider outside ProvidersController
      // is warranted: we are inside the provider implementation, the URL comes from
      // the list ProvidersController itself handed us, and the instance is destroyed
      // before we return. Reusing `this` instead would hit ethers' 250ms #perform
      // cache and hand every endpoint the same cached `null`.
      const probe = new JsonRpcProvider(url, this.#network, {
        staticNetwork: this.#network ?? undefined,
        batchMaxCount: 1
      })

      try {
        // eslint-disable-next-line no-await-in-loop
        const receipt = await probe.getTransactionReceipt(txnId)
        if (!receipt) continue

        // This endpoint knows something the active one does not - prefer it from
        // now on, same as a successful failover in `_send`.
        this.#activeIndex = idx
        this.#lastSwitchAt = Date.now()
        console.warn(
          `[FailoverRPC] chain ${this.#chainIdLabel}: receipt for ${txnId} found on ${url} ` +
            'but not on the active endpoint - switching'
        )

        return receipt
      } catch {
        // Same as in `_send`: a dead endpoint is just the next one's turn.
      } finally {
        probe.destroy()
      }
    }

    return null
  }

  /**
   * Push an already signed raw txn to every endpoint except the one that is
   * currently active.
   *
   * `_send` stops at the first endpoint that answers, which is the right thing for
   * reads but leaves a broadcast at the mercy of a single node: a txn accepted by
   * one PulseChain endpoint has been observed sitting in its mempool for >10min
   * without ever propagating (see docs/transactions.md), and it only got mined
   * after being re-sent by hand to another endpoint. Re-sending a signed txn is
   * idempotent — same hash, same nonce — so the worst case is an "already known"
   * reply, which is ignored along with every other per-endpoint failure.
   */
  async rebroadcastToOtherUrls(rawTxn: string): Promise<void> {
    const payload = JSON.stringify({
      id: 1,
      jsonrpc: '2.0',
      method: 'eth_sendRawTransaction',
      params: [rawTxn]
    })

    await Promise.all(
      this.#urls.map(async (url, idx) => {
        if (idx === this.#activeIndex || !url) return

        try {
          const request = new FetchRequest(url)
          request.body = payload
          request.setHeader('content-type', 'application/json')
          request.timeout = this.#perRequestTimeout
          const response = await request.send()
          response.assertOk()
        } catch (error: any) {
          // Every endpoint here is a best-effort extra: an outage, a rate limit or
          // an "already known" reply are all equally uninteresting.
          console.warn(
            `[FailoverRPC] chain ${this.#chainIdLabel}: rebroadcast to ${url} failed:`,
            error?.message || error
          )
        }
      })
    )
  }
}

/**
 * Second-opinion receipt lookup for providers that carry a list of endpoints.
 * Returns null for any other provider, so callers can treat it as "no extra
 * information available".
 */
export async function getTransactionReceiptFromBackupRpcs(
  provider: unknown,
  txnId: string
): Promise<TransactionReceipt | null> {
  if (!(provider instanceof FailoverJsonRpcProvider)) return null

  return provider.getTransactionReceiptFromOtherUrls(txnId)
}

/**
 * Best-effort "make sure the network actually has this txn" helper for providers
 * that carry a list of endpoints. A no-op for any other provider.
 */
export async function rebroadcastToBackupRpcs(provider: unknown, rawTxn: string): Promise<void> {
  if (!(provider instanceof FailoverJsonRpcProvider)) return

  await provider.rebroadcastToOtherUrls(rawTxn)
}
